/**
 * Top-level reviewer dashboard data: every valuer, every report, and every result row
 * (for the at-a-glance summaries on Home + the valuer's reports screen). Loads once, then
 * stays live via Realtime — inserts/updates upsert into local state, deletes remove — so
 * counts and status chips update with no refresh (see the plan's "live signals").
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchAllCheckResults,
  fetchAllErrorFindings,
  fetchAllRiskResults,
  fetchBanks,
  fetchCheckSchema,
  fetchReports,
  fetchRiskSchema,
  fetchValuers,
  mapCheckResult,
  mapErrorFinding,
  mapReport,
  mapRiskResult,
  subscribeReviewerRealtime,
  type RawCheckResult,
  type RawErrorFinding,
  type RawReport,
  type RawRiskResult,
} from './queries';
import type {
  BankRow,
  CheckResultRow,
  CheckSchemaRow,
  ErrorFindingRow,
  ReportRow,
  RiskResultRow,
  RiskSchemaRow,
  ValuerRow,
} from './types';

interface DashboardData {
  valuers: ValuerRow[];
  banks: BankRow[];
  reports: ReportRow[];
  checkSchema: CheckSchemaRow[];
  riskSchema: RiskSchemaRow[];
  checkResults: CheckResultRow[];
  riskResults: RiskResultRow[];
  errorFindings: ErrorFindingRow[];
}

type State =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | ({ status: 'ready' } & DashboardData);

function upsertById<T extends { id: string }>(list: T[], row: T): T[] {
  const idx = list.findIndex((x) => x.id === row.id);
  if (idx === -1) return [row, ...list];
  const next = list.slice();
  next[idx] = row;
  return next;
}

function removeById<T extends { id: string }>(list: T[], id: string): T[] {
  return list.filter((x) => x.id !== id);
}

export function useReviewerData() {
  const [state, setState] = useState<State>({ status: 'loading' });
  const dataRef = useRef<DashboardData | null>(null);

  const load = useCallback(async () => {
    setState({ status: 'loading' });
    try {
      const [valuers, banks, reports, checkSchema, riskSchema, checkResults, riskResults, errorFindings] =
        await Promise.all([
          fetchValuers(),
          fetchBanks(),
          fetchReports(),
          fetchCheckSchema(),
          fetchRiskSchema(),
          fetchAllCheckResults(),
          fetchAllRiskResults(),
          fetchAllErrorFindings(),
        ]);
      const next: DashboardData = {
        valuers,
        banks,
        reports,
        checkSchema,
        riskSchema,
        checkResults,
        riskResults,
        errorFindings,
      };
      dataRef.current = next;
      setState({ status: 'ready', ...next });
    } catch (err) {
      setState({ status: 'error', message: err instanceof Error ? err.message : 'Could not load the dashboard.' });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const unsubscribe = subscribeReviewerRealtime((table, eventType, row, oldRow) => {
      const current = dataRef.current;
      if (!current) return;
      let next: DashboardData = current;

      if (table === 'reports') {
        if (eventType === 'DELETE') {
          next = { ...current, reports: removeById(current.reports, (oldRow as { id: string }).id) };
        } else {
          next = { ...current, reports: upsertById(current.reports, mapReport(row as unknown as RawReport)) };
        }
      } else if (table === 'check_results') {
        if (eventType === 'DELETE') {
          next = { ...current, checkResults: removeById(current.checkResults, (oldRow as { id: string }).id) };
        } else {
          next = { ...current, checkResults: upsertById(current.checkResults, mapCheckResult(row as unknown as RawCheckResult)) };
        }
      } else if (table === 'risk_results') {
        if (eventType === 'DELETE') {
          next = { ...current, riskResults: removeById(current.riskResults, (oldRow as { id: string }).id) };
        } else {
          next = { ...current, riskResults: upsertById(current.riskResults, mapRiskResult(row as unknown as RawRiskResult)) };
        }
      } else if (table === 'error_findings') {
        if (eventType === 'DELETE') {
          next = { ...current, errorFindings: removeById(current.errorFindings, (oldRow as { id: string }).id) };
        } else {
          next = { ...current, errorFindings: upsertById(current.errorFindings, mapErrorFinding(row as unknown as RawErrorFinding)) };
        }
      }

      dataRef.current = next;
      setState((prev) => (prev.status === 'ready' ? { status: 'ready', ...next } : prev));
    });
    return unsubscribe;
  }, []);

  return { state, reload: load };
}
